import type {QueryKey} from '@tanstack/query-core';
import type {UseQueryOptions, UseQueryResult} from '@tanstack/react-query/src/types';
import {useQuery} from '@tanstack/react-query';
import {useEffect} from 'react';
import type {Payload} from '@interfaces/api';
import RequestError from '@errors/RequestError';
import {useLocation, useNavigate} from 'react-router-dom';

export default function useQueryWithAuth<
    TQueryFnData extends Payload = Payload,
    TError = unknown,
    TData extends TQueryFnData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
>(
    options: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'initialData'> & {
        initialData?: () => undefined;
    },
): UseQueryResult<TData, TError> {
    const queryResult = useQuery<TQueryFnData, TError, TData, TQueryKey>(options);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (
            queryResult.isError &&
            queryResult.error instanceof RequestError &&
            queryResult.error.isAuthorizationError()
        ) {
            navigate('/login', {
                replace: true,
                state: {redirectTo: location.pathname},
            });
        }
    }, [queryResult.isError]);

    return queryResult;
}
